import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_SLEEP_CLOTHES,
  DEFAULT_SLEEP_PLACES,
  DEFAULT_SLEEP_POSITIONS,
  EVENT_META,
  activeMinutes,
  breastSides,
  can,
  formatClock,
  formatDuration,
  type BabyEvent,
  type EventType,
} from '@bb/shared/core';
import { Chips, Icon, Seg, Sheet } from '../components';
import { startBottle, startBreast, startSettling, startTimer } from '../actions';
import { useApp } from '../state';
import { ALARM_DEFAULT, RunningPanel, runningTitle } from './Running';

const SPECIAL: EventType[] = ['breast', 'bottle', 'sleep', 'walk'];
import { DurationWheel, formatIn } from '../Wheel';

/**
 * Шторка действия: для таймерных видов — запуск с подсказками или управление идущим таймером;
 * для остальных — обычная форма записи.
 */
export function ActionSheet({ type, event, onClose }: { type: EventType; event?: BabyEvent; onClose: () => void }) {
  const ctx = useApp();
  // Берём таймер только из свежих данных: после «Стоп» он исчезает — и шторка закрывается.
  const running = event && !event.endedAt ? ctx.running.find((r) => r.id === event.id) : ctx.running.find((r) => r.type === type);
  const canWrite = can(ctx.family.role, 'events.write');

  // Для купания, гимнастики, плача и своих событий отдельного сценария нет: если не идёт — сразу в редактор
  // (там «Сейчас, таймер» или «Задним числом»).
  const special = SPECIAL.includes(type);
  const toEditor = !EVENT_META[type].timed || (!special && !running);
  useEffect(() => {
    if (toEditor) {
      onClose();
      ctx.openEditor({ type });
    }
  }, [type]);
  // Таймер остановили (здесь или на другом устройстве) — закрываем шторку.
  const [wasRunning, setWasRunning] = useState(Boolean(running));
  useEffect(() => {
    if (running) setWasRunning(true);
    else if (wasRunning) onClose();
  }, [running, wasRunning, onClose]);

  if (toEditor && !wasRunning) return null;
  const title = running ? runningTitle(running) : type === 'breast' ? 'Кормление грудью' : type === 'bottle' ? 'Бутылочка' : type === 'sleep' ? 'Сон' : type === 'walk' ? 'Прогулка' : EVENT_META[type].label;

  return (
    <Sheet title={title} onClose={onClose}>
      {running ? (
        <RunningPanel e={running} canWrite={canWrite} />
      ) : type === 'breast' ? (
        <BreastStart onDone={onClose} />
      ) : type === 'bottle' ? (
        <BottleStart onDone={onClose} />
      ) : type === 'sleep' ? (
        <SleepStart onDone={onClose} />
      ) : type === 'walk' ? (
        <WalkStart onDone={onClose} />
      ) : null}
      {!running && special && (
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            onClose();
            ctx.openEditor({ type, preset: undefined });
          }}
        >
          Записать задним числом
        </button>
      )}
    </Sheet>
  );
}

/** Последняя законченная запись вида (из последних 48 часов или «последних» с сервера). */
function useLast(type: EventType): BabyEvent | null {
  const { recent } = useApp();
  return useMemo(() => {
    const pool = [...(recent?.events ?? []), ...(recent?.last ?? [])].filter((e) => e.type === type && e.endedAt);
    pool.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
    return pool[0] ?? null;
  }, [recent, type]);
}

function ago(iso: string) {
  const m = (Date.now() - Date.parse(iso)) / 60000;
  return m < 1 ? 'только что' : `${formatDuration(m)} назад`;
}

/** «Напомнить через»: выключатель и барабан. value — секунды (0 — без напоминания). */
function AlarmPick({ type, value, onChange }: { type: string; value: number; onChange: (v: number) => void }) {
  const def = ALARM_DEFAULT[type] ?? ALARM_DEFAULT.walk!;
  const [last, setLast] = useState(def.sec);
  const on = value > 0;
  return (
    <div className="field alarm-box">
      <div className="row">
        <span className="label grow">
          <Icon name="bell" size={14} /> {type === 'breast' ? 'Таймер на грудь' : 'Таймер'}
        </span>
        <Seg
          label="Таймер"
          value={on ? 'on' : 'off'}
          options={[
            { value: 'off', label: 'Нет' },
            { value: 'on', label: 'Да' },
          ]}
          onChange={(v) => onChange(v === 'on' ? last : 0)}
        />
      </div>
      {on && (
        <>
          <DurationWheel
            seconds={value}
            mode={def.mode}
            onChange={(s) => {
              setLast(s);
              onChange(s);
            }}
          />
          <span className="tiny muted">
            {type === 'breast'
              ? `${formatIn(value)} на каждую грудь: после смены отсчёт начнётся заново. В ноль — сигнал, из него можно сразу сменить грудь.`
              : `Пойдёт обратный отсчёт ${formatIn(value)}. В ноль — сигнал на экране и сообщение от бота, даже если приложение закрыто.`}
          </span>
        </>
      )}
    </div>
  );
}

function BreastStart({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const last = useLast('breast');
  const lastSide = last ? breastSides(last).last : null;
  const [alarm, setAlarm] = useState(0);
  const start = async (side: 'left' | 'right') => {
    await startBreast(ctx, side, alarm || null);
    onDone();
  };
  return (
    <div className="stack">
      {last ? (
        <div className="last-card">
          <div className="small muted">Прошлое кормление</div>
          <div style={{ fontWeight: 700 }}>
            {ago(last.endedAt!)} · в {formatClock(Date.parse(last.startedAt), ctx.family.tz)}
          </div>
          <div className="small">
            {(() => {
              const s = breastSides(last);
              const parts = [s.left >= 0.5 ? `левая ${formatDuration(s.left)}` : '', s.right >= 0.5 ? `правая ${formatDuration(s.right)}` : ''].filter(Boolean);
              return `${parts.join(' · ') || `${formatDuration(activeMinutes(last))}`} · всего ${formatDuration(activeMinutes(last))}`;
            })()}
          </div>
          {lastSide && (
            <div className="hint">
              Закончили на <b>{lastSide === 'left' ? 'левой' : 'правой'}</b> — начните с неё
            </div>
          )}
        </div>
      ) : (
        <p className="small muted">Это первое кормление в дневнике.</p>
      )}
      <div className="tiles2">
        {(['left', 'right'] as const).map((side) => (
          <button
            key={side}
            type="button"
            className={`side-btn${lastSide === side ? ' rec' : ''}`}
            onClick={() => void start(side)}
            data-testid={`start-${side}`}
          >
            <span className="side-letter">{side === 'left' ? 'Л' : 'П'}</span>
            {side === 'left' ? 'Левая' : 'Правая'}
            <span className="side-go">
              <Icon name="play" size={12} /> {lastSide === side ? 'начать · последняя' : 'начать'}
            </span>
          </button>
        ))}
      </div>
      <p className="tiny muted hint-line">
        👆 Нажмите на грудь — таймер запустится сразу. Пауза и смена стороны — во время кормления.
      </p>
      <AlarmPick type="breast" value={alarm} onChange={setAlarm} />
    </div>
  );
}

function BottleStart({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const last = useLast('bottle');
  const lastPrepared = Number(last?.payload.preparedMl ?? last?.payload.ml) || 120;
  const [ml, setMl] = useState(lastPrepared);
  const [kind, setKind] = useState(String(last?.payload.kind ?? 'formula'));
  const [alarm, setAlarm] = useState(0);
  return (
    <div className="stack">
      {last && (
        <div className="last-card">
          <div className="small muted">Прошлая бутылочка</div>
          <div style={{ fontWeight: 700 }}>
            {ago(last.endedAt!)} · в {formatClock(Date.parse(last.startedAt), ctx.family.tz)}
          </div>
          <div className="small">
            Выпил {String(last.payload.ml)} мл
            {typeof last.payload.preparedMl === 'number' ? ` из ${last.payload.preparedMl}` : ''}
            {activeMinutes(last) >= 1 ? ` · за ${formatDuration(activeMinutes(last))}` : ''}
          </div>
        </div>
      )}
      <div className="field">
        <span className="label">Сколько намешали, мл</span>
        <div className="stepper">
          <button type="button" aria-label="Меньше на 10 мл" onClick={() => setMl(Math.max(10, ml - 10))}>
            −
          </button>
          <span className="num" data-testid="ml">
            {ml}
          </span>
          <button type="button" className="plus" aria-label="Больше на 10 мл" onClick={() => setMl(Math.min(500, ml + 10))}>
            +
          </button>
        </div>
      </div>
      <Seg
        label="Что в бутылочке"
        value={kind}
        options={[
          { value: 'formula', label: 'Смесь' },
          { value: 'breast_milk', label: 'Сцеженное' },
        ]}
        onChange={setKind}
      />
      <AlarmPick type="bottle" value={alarm} onChange={setAlarm} />
      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={async () => {
          await startBottle(ctx, ml, kind, alarm || null);
          onDone();
        }}
      >
        <Icon name="play" size={18} /> Начать кормление
      </button>
    </div>
  );
}

function SleepStart({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const last = useLast('sleep');
  const fs = ctx.family.settings ?? {};
  const places = fs.sleepPlaces?.length ? fs.sleepPlaces : DEFAULT_SLEEP_PLACES;
  const clothesList = fs.sleepClothes?.length ? fs.sleepClothes : DEFAULT_SLEEP_CLOTHES;
  const positions = fs.sleepPositions?.length ? fs.sleepPositions : DEFAULT_SLEEP_POSITIONS;
  const lp = last?.payload ?? {};
  const [place, setPlace] = useState<string | null>(typeof lp.place === 'string' ? lp.place : null);
  const [position, setPosition] = useState<string | null>(typeof lp.position === 'string' ? lp.position : null);
  const [clothes, setClothes] = useState<string[]>(Array.isArray(lp.clothes) ? (lp.clothes as string[]) : []);
  const [temp, setTemp] = useState<number | null>(typeof lp.roomTemp === 'number' ? lp.roomTemp : null);
  const [alarm, setAlarm] = useState(0);
  const [more, setMore] = useState(false);
  const opts = (xs: string[]) => xs.map((x) => ({ value: x, label: x }));

  async function go(withDetails: boolean, settle = false) {
    const payload: Record<string, unknown> = {};
    if (withDetails) {
      if (place) payload.place = place;
      if (position) payload.position = position;
      if (clothes.length) payload.clothes = clothes;
      if (temp != null) payload.roomTemp = temp;
    }
    if (alarm) {
      payload.alarmAt = new Date(Date.now() + alarm * 1000).toISOString();
      payload.alarmFrom = new Date().toISOString();
    }
    if (settle) await startSettling(ctx, payload);
    else await startTimer(ctx, 'sleep', payload);
    onDone();
  }

  return (
    <div className="stack">
      {last && (
        <div className="last-card">
          <div className="small muted">Прошлый сон</div>
          <div style={{ fontWeight: 700 }}>
            проснулся {ago(last.endedAt!)} · спал {formatDuration(activeMinutes(last))}
          </div>
        </div>
      )}
      <div className="field">
        <span className="label">Где спит</span>
        <Chips label="Где спит" value={place} options={opts(places)} onChange={(v) => setPlace(v === place ? null : v)} />
      </div>
      <button type="button" className="link-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setMore(!more)} aria-expanded={more}>
        {more ? 'Скрыть подробности' : 'Одежда, положение, температура…'}
      </button>
      {more && (
        <>
          <div className="field">
            <span className="label">Как лежит</span>
            <Chips label="Как лежит" value={position} options={opts(positions)} onChange={(v) => setPosition(v === position ? null : v)} />
          </div>
          <div className="field">
            <span className="label">Одежда (можно несколько)</span>
            <div className="chips" role="group" aria-label="Одежда">
              {clothesList.map((c) => (
                <button key={c} type="button" className="chip" aria-pressed={clothes.includes(c)} onClick={() => setClothes(clothes.includes(c) ? clothes.filter((x) => x !== c) : [...clothes, c])}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="label">Температура в комнате</span>
            <div className="stepper">
              <button type="button" aria-label="Холоднее" onClick={() => setTemp(Math.max(14, (temp ?? 22) - 0.5))}>
                −
              </button>
              <span className="num" style={{ fontSize: 30 }}>
                {temp == null ? '—' : `${String(temp).replace('.', ',')}°`}
              </span>
              <button type="button" className="plus" aria-label="Теплее" onClick={() => setTemp(Math.min(30, (temp ?? 21.5) + 0.5))}>
                +
              </button>
            </div>
          </div>
          <p className="tiny muted">Списки можно поменять: Настройки → Справочники.</p>
        </>
      )}
      <AlarmPick type="sleep" value={alarm} onChange={setAlarm} />
      <div className="tiles2">
        <button type="button" className="btn btn-soft" onClick={() => void go(true, true)} data-testid="start-settle" style={{ minHeight: 64 }}>
          <Icon name="play" size={16} /> Начали укладывать
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void go(true)} data-testid="start-sleep" style={{ minHeight: 64 }}>
          <Icon name="sleep" size={18} /> Уже уснул
        </button>
      </div>
      <p className="tiny muted hint-line">«Начали укладывать» — засечём, сколько уходит на укладывание и что помогает. Потом нажмите «Уснул!».</p>
    </div>
  );
}

function WalkStart({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const [alarm, setAlarm] = useState(0);
  return (
    <div className="stack">
      <AlarmPick type="walk" value={alarm} onChange={setAlarm} />
      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={async () => {
          await startTimer(ctx, 'walk', alarm ? { alarmAt: new Date(Date.now() + alarm * 1000).toISOString(), alarmFrom: new Date().toISOString() } : {});
          onDone();
        }}
      >
        <Icon name="walk" size={18} /> Вышли гулять
      </button>
    </div>
  );
}
